/*
 * BNR Sustainability Tracker — production image slider.
 *
 * Progressive enhancement:
 * - Without JS, the native scroll-snap track is manually swipeable.
 * - With JS:
 *   - Auto-advances ONLY after the next slide's image is fully loaded and
 *     decoded (no flashes of empty slides on slow networks).
 *   - Pre-warms the slide after next while the user looks at the current one.
 *   - Pauses on hover / focus.
 *   - Pauses when the slider is off-screen (IntersectionObserver).
 *   - Respects prefers-reduced-motion (no auto-advance).
 *   - Re-inits after Shopify theme-editor re-renders.
 */
(function () {
  'use strict';

  var prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function imageLoaded(img) {
    return !!img && img.complete && img.naturalWidth > 0;
  }

  /**
   * Resolves once an image is fully loaded AND decoded (ready to paint
   * without jank). Kicks lazy loading to eager on demand. Resolves on
   * error too so a broken image doesn't stall the slider forever.
   */
  function whenReady(img) {
    if (!img) return Promise.resolve();
    if (imageLoaded(img)) {
      if (typeof img.decode === 'function') {
        return img.decode().catch(function () {});
      }
      return Promise.resolve();
    }
    if (img.loading === 'lazy') img.loading = 'eager';
    return new Promise(function (resolve) {
      var cleanup = function () {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', cleanup);
        resolve();
      };
      var onLoad = function () {
        if (typeof img.decode === 'function') {
          img.decode().then(cleanup, cleanup);
        } else {
          cleanup();
        }
      };
      img.addEventListener('load', onLoad);
      img.addEventListener('error', cleanup);
    });
  }

  function initSlider(root) {
    if (root.dataset.bnrSustSliderReady === '1') return;
    root.dataset.bnrSustSliderReady = '1';

    var track = root.querySelector('[data-slider-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-slide-dot]'));
    if (slides.length <= 1) return;

    var imgs = slides.map(function (slide) {
      return slide.querySelector('img');
    });

    var interval = parseInt(root.dataset.slideInterval, 10);
    if (!interval || isNaN(interval)) interval = 5000;
    var autoplay = root.dataset.autoplay === 'on';

    var currentIndex = 0;
    var timerId = null;
    var paused = false;
    var visible = false;
    var advancing = false;

    function wrap(i) {
      var count = slides.length;
      return ((i % count) + count) % count;
    }

    function markLoaded(index) {
      var slide = slides[index];
      if (slide && !slide.classList.contains('is-loaded')) {
        slide.classList.add('is-loaded');
      }
    }

    function preload(index) {
      var i = wrap(index);
      whenReady(imgs[i]).then(function () {
        markLoaded(i);
      });
    }

    // Mark slides that are already cached/loaded synchronously on init.
    for (var j = 0; j < imgs.length; j++) {
      if (imageLoaded(imgs[j])) markLoaded(j);
    }

    function setActiveDot(index) {
      for (var i = 0; i < dots.length; i++) {
        if (i === index) {
          dots[i].setAttribute('aria-current', 'true');
        } else {
          dots[i].removeAttribute('aria-current');
        }
      }
    }

    function scrollTo(index, behavior) {
      var slide = slides[index];
      if (!slide) return;
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: behavior || (prefersReducedMotion ? 'auto' : 'smooth'),
      });
    }

    function goTo(index, behavior) {
      var next = wrap(index);
      currentIndex = next;
      scrollTo(next, behavior);
      setActiveDot(next);
      // Pre-warm the next-next so it's ready when auto-advance fires.
      preload(next + 1);
    }

    function scheduleNext() {
      if (!autoplay || prefersReducedMotion) return;
      stop();
      timerId = setTimeout(tick, interval);
    }

    function tick() {
      if (paused || !visible || advancing) {
        scheduleNext();
        return;
      }
      var nextIdx = wrap(currentIndex + 1);
      advancing = true;
      // Wait for the upcoming image to be ready BEFORE scrolling to it.
      whenReady(imgs[nextIdx]).then(function () {
        markLoaded(nextIdx);
        advancing = false;
        if (paused || !visible) {
          scheduleNext();
          return;
        }
        goTo(nextIdx);
        scheduleNext();
      });
    }

    function stop() {
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    }

    for (var i = 0; i < dots.length; i++) {
      (function (idx) {
        dots[idx].addEventListener('click', function () {
          paused = false;
          // Kick loading for the requested slide + neighbours.
          preload(idx);
          preload(idx + 1);
          goTo(idx);
          scheduleNext();
        });
      })(i);
    }

    root.addEventListener('mouseenter', function () { paused = true; });
    root.addEventListener('mouseleave', function () { paused = false; });
    root.addEventListener('focusin', function () { paused = true; });
    root.addEventListener('focusout', function () { paused = false; });

    // Sync dots to manual scrolling (touch / trackpad).
    var scrollTimer = null;
    track.addEventListener('scroll', function () {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var slideWidth = slides[0].offsetWidth;
        if (slideWidth <= 0) return;
        var idx = Math.round(track.scrollLeft / slideWidth);
        if (idx !== currentIndex && slides[idx]) {
          currentIndex = idx;
          setActiveDot(idx);
          preload(idx + 1);
        }
      }, 120);
    }, { passive: true });

    // Start / stop based on viewport visibility to save CPU + network.
    if (typeof window.IntersectionObserver === 'function') {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible = entry.isIntersecting;
          if (visible) {
            // Kick the first two images as soon as the slider is visible.
            preload(currentIndex);
            preload(currentIndex + 1);
            scheduleNext();
          } else {
            stop();
          }
        });
      }, { threshold: 0.25 });
      observer.observe(root);
    } else {
      visible = true;
      preload(currentIndex);
      preload(currentIndex + 1);
      scheduleNext();
    }
  }

  function initAll() {
    var sliders = document.querySelectorAll('[data-bnr-sust-slider]');
    for (var i = 0; i < sliders.length; i++) {
      initSlider(sliders[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (typeof document.addEventListener === 'function') {
    document.addEventListener('shopify:section:load', initAll);
    document.addEventListener('shopify:section:reorder', initAll);
  }
})();
